import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useApi, useAction } from '../lib/hooks.js';
import { Loading, ErrorNote, StatusPill, Modal, formatQty } from '../components/ui.jsx';
import ItemPicker from '../components/ItemPicker.jsx';

/**
 * How long ago the shelf behind a "probably" was last confirmed.
 *
 * The analysis answers per ingredient and reports the age of the least certain
 * row backing it, which is the one that made the verdict "probably" -- saying
 * that out loud is what turns a hedge into something somebody can act on.
 */
function lastChecked(status) {
  const days = status.checked_days_ago;
  if (days === null || days === undefined) return 'a while ago';
  if (days <= 0) return 'today';
  return `${days}d ago`;
}

/** Req #8/#10: read a recipe and see it against the pantry. */
export default function RecipeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [servings, setServings] = useState(null);

  const recipe = useApi(`/recipes/${id}`);
  const analysis = useApi(`/recipes/${id}/analyze${servings ? `?servings=${servings}` : ''}`);
  const nutrition = useApi(`/recipes/${id}/nutrition`);
  const [shopping, setShopping] = useState(false);
  const [matching, setMatching] = useState(null);
  const [cooked, setCooked] = useState(null);
  const { busy, error, run } = useAction();

  if (recipe.loading && !recipe.data) return <Loading rows={6} />;
  if (recipe.error) return <ErrorNote error={recipe.error} onRetry={recipe.reload} />;
  if (!recipe.data) return null;

  const data = recipe.data;
  const summary = analysis.data?.summary;
  const byIngredientId = Object.fromEntries(
    (analysis.data?.ingredients || []).map((i) => [i.ingredient_id, i]),
  );
  const scale = servings && data.servings ? servings / data.servings : 1;

  const remove = () => run(async () => {
    if (!window.confirm(`Delete “${data.title}”?`)) return;
    await api.delete(`/recipes/${id}`);
    navigate('/recipes');
  });

  // What cooking took off the shelves, kept until the next action so the undo
  // stays reachable for as long as the summary is on screen.
  const markCooked = () => run(async () => {
    const result = await api.post(`/recipes/${id}/cooked`, { servings });
    setCooked(result);
    recipe.reload();
    analysis.reload();
  });

  const undoCooked = () => run(async () => {
    await api.post(`/recipes/${id}/uncook`, {
      event_ids: (cooked?.deductions || []).map((d) => d.event_id).filter(Boolean),
    });
    setCooked(null);
    recipe.reload();
    await analysis.reload();
  });

  // Every confirmed match is recorded as an alias, so the correction compounds:
  // the README's "every confirmation teaches the system" finally includes recipes.
  // The reload is awaited so `busy` holds until the fresh rows land — otherwise
  // the stale suggestion chips re-enable for a beat and a second tap could
  // teach a second alias for the same row.
  const setLink = (ingredientId, masterItemId) => run(async () => {
    await api.patch(`/recipes/${id}/ingredients/${ingredientId}`, { master_item_id: masterItemId });
    await analysis.reload();
  });

  // Both halves in one request: the shelves are emptied and the food joins the
  // active shopping list through the same merge the recipe's own "add what I
  // need" uses, so saying it twice does not list it twice.
  const imOut = (status) => run(async () => {
    if (!status.master_item_id) return;
    await api.post('/inventory/consume-item', { master_item_id: status.master_item_id });
    await analysis.reload();
  });

  const groups = [];
  for (const ingredient of data.ingredients) {
    const name = ingredient.group_name || '';
    let group = groups.find((g) => g.name === name);
    if (!group) { group = { name, items: [] }; groups.push(group); }
    group.items.push(ingredient);
  }

  return (
    <div className="stack">
      <ErrorNote error={error} />

      {data.image_url && (
        /* Wrapped so the theme owns the crop and the framing: Market runs it
           to the edge, Homestead mats it. */
        <figure className="hero-figure" style={{ margin: 0 }}>
          <img src={data.image_url} alt="" />
        </figure>
      )}

      <div>
        <h1>{data.title}</h1>
        <div className="small muted" style={{ marginTop: 4 }}>
          {data.source_url
            ? <a href={data.source_url} target="_blank" rel="noreferrer">{data.source_name || 'source'}</a>
            : data.source_name}
          {data.total_minutes ? ` · ${data.total_minutes} min` : ''}
          {data.servings ? ` · serves ${data.servings}` : ''}
          {data.times_cooked > 0 ? ` · cooked ${data.times_cooked}×` : ''}
        </div>
      </div>

      {data.description && <p className="small">{data.description}</p>}

      {data.tags?.length > 0 && (
        <div className="chips">
          {data.tags.map((tag) => <span key={tag} className="chip">{tag}</span>)}
        </div>
      )}

      {summary && (
        <div className={`alert ${summary.can_make ? 'info' : 'warn'}`}>
          {summary.can_make
            ? (
              <strong>
                {summary.probably > 0
                  ? `You probably have everything — ${summary.probably} worth checking.`
                  : 'You have everything for this.'}
              </strong>
            )
            : (
              <strong>
                {summary.missing + summary.short > 0 && `${summary.missing + summary.short} to buy`}
                {summary.missing + summary.short > 0 && summary.unknown > 0 && ', '}
                {summary.unknown > 0 && `${summary.unknown} I can't verify`}
              </strong>
            )}
          <div className="small" style={{ marginTop: 4 }}>
            {summary.have} of {summary.required} required ingredients on hand
          </div>
        </div>
      )}

      {data.servings && (
        <div className="row">
          <span className="small muted">Scale to</span>
          <div className="chips grow">
            {[data.servings / 2, data.servings, data.servings * 2].map((count) => {
              const rounded = Math.round(count * 10) / 10;
              if (rounded < 1) return null;
              const active = (servings || data.servings) === rounded;
              return (
                <button
                  key={rounded}
                  type="button"
                  className={`chip ${active ? 'active' : ''}`}
                  onClick={() => setServings(rounded === data.servings ? null : rounded)}
                >
                  {rounded} servings
                </button>
              );
            })}
          </div>
        </div>
      )}

      <section>
        <div className="section-title">
          <h2>Ingredients</h2>
          <span className="count">{data.ingredients.length}</span>
        </div>
        {groups.map((group) => (
          <div key={group.name} style={{ marginBottom: 12 }}>
            {group.name && <h3 style={{ margin: '10px 0 6px' }}>{group.name}</h3>}
            <div className="card list">
              {group.items.map((ingredient) => {
                const status = byIngredientId[ingredient.id];
                return (
                  <div key={ingredient.id} className="list-item">
                    <div className="grow" style={{ minWidth: 0 }}>
                      <div className="truncate">
                        {ingredient.quantity != null && (
                          <strong>{formatQty(ingredient.quantity * scale, ingredient.unit)} </strong>
                        )}
                        {ingredient.item_name || ingredient.raw_text}
                        {ingredient.optional && <span className="muted small"> (optional)</span>}
                      </div>
                      {status?.status === 'unknown' && (
                        <div className="tiny muted truncate">{status.detail || status.reason}</div>
                      )}
                      {status?.locations?.length > 0 && (status.status === 'have' || status.status === 'probably') && (
                        <div className="tiny muted truncate">
                          in {status.locations.join(', ')}
                          {/* M4: why it says "probably". The number behind the
                              word is a decay curve nobody wants to read, so the
                              row says the thing the curve is made of. */}
                          {status.status === 'probably' && ` · last checked ${lastChecked(status)}`}
                        </div>
                      )}
                      {ingredient.preparation && (
                        <div className="tiny muted truncate">{ingredient.preparation}</div>
                      )}
                      {status && !status.master_item_id && status.suggestions?.length > 0 && (
                        <div className="chips" style={{ marginTop: 6 }}>
                          {status.suggestions.map((suggestion) => (
                            <button
                              key={suggestion.id}
                              type="button"
                              className="chip"
                              disabled={busy}
                              onClick={() => setLink(ingredient.id, suggestion.id)}
                            >
                              {suggestion.name} <span className="tiny">{Math.round(suggestion.score * 100)}%</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* M7: the correction somebody is standing in front of the
                        shelf to make. It empties every row of that item and
                        puts the food on the shopping list in one tap, because
                        an "I'm out" that leaves the list untouched is how a
                        household ends up cooking without it twice. */}
                    {status && (status.status === 'have' || status.status === 'probably') && (
                      <button
                        type="button"
                        className="small ghost"
                        disabled={busy}
                        onClick={() => imOut(status)}
                      >
                        I&rsquo;m out
                      </button>
                    )}
                    {status && (
                      <button
                        type="button"
                        className="small ghost"
                        disabled={busy}
                        aria-label={`${status.master_item_id ? 'Change the match for' : 'Match'} ${ingredient.item_name || ingredient.raw_text}`}
                        onClick={() => setMatching(status)}
                      >
                        {status.master_item_id ? 'change' : 'match…'}
                      </button>
                    )}
                    {status && !ingredient.optional && <StatusPill status={status.status} />}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      {summary && !summary.can_make && (
        <button type="button" className="primary full" onClick={() => setShopping(true)}>
          Add what I need to the shopping list
        </button>
      )}

      {data.instructions && (
        <section>
          <div className="section-title"><h2>Method</h2></div>
          <div className="card recipe-instructions">{data.instructions}</div>
        </section>
      )}

      {data.notes && (
        <section>
          <div className="section-title"><h2>Notes</h2></div>
          <div className="card small">{data.notes}</div>
        </section>
      )}

      {nutrition.data && nutrition.data.covered_ingredients > 0 && (
        <section>
          <div className="section-title"><h2>Nutrition (estimated)</h2></div>
          <div className="card">
            <div className="stat-grid">
              <div className="stat">
                <div className="value">{Math.round(nutrition.data.per_serving.calories)}</div>
                <div className="label">cal / serving</div>
              </div>
              <div className="stat">
                <div className="value">{Math.round(nutrition.data.per_serving.protein_g)}g</div>
                <div className="label">protein</div>
              </div>
              <div className="stat">
                <div className="value">{Math.round(nutrition.data.per_serving.carbs_g)}g</div>
                <div className="label">carbs</div>
              </div>
              <div className="stat">
                <div className="value">{Math.round(nutrition.data.per_serving.fat_g)}g</div>
                <div className="label">fat</div>
              </div>
            </div>
            {/* Partial coverage is stated outright rather than presenting an
                incomplete sum as a total. */}
            {!nutrition.data.complete && (
              <p className="tiny muted" style={{ marginTop: 10, marginBottom: 0 }}>
                Based on {nutrition.data.covered_ingredients} of {nutrition.data.total_ingredients} ingredients.
                Link nutrition data to the rest for a full figure.
              </p>
            )}
          </div>
        </section>
      )}

      {cooked && <CookedNote result={cooked} busy={busy} onUndo={undoCooked} />}

      <div className="row wrap" style={{ marginTop: 8 }}>
        <Link className="button grow" to={`/recipes/${id}/edit`}>Edit</Link>
        <button type="button" className="grow" disabled={busy} onClick={markCooked}>Cooked it</button>
        <button type="button" className="danger" disabled={busy} onClick={remove}>Delete</button>
      </div>

      {shopping && (
        <AddToList
          recipeId={id}
          servings={servings}
          onClose={() => setShopping(false)}
        />
      )}

      {matching && (
        <MatchIngredient
          recipeId={id}
          row={matching}
          onClose={() => setMatching(null)}
          onSaved={() => { setMatching(null); analysis.reload(); }}
        />
      )}
    </div>
  );
}

/**
 * What the last "Cooked it" took off the shelves, and the way back.
 *
 * Deducting silently would be the worse half of a good idea: the shelves would
 * drift and nobody would know which tap did it. So the deduction is stated in
 * the same words somebody would use -- "used 2 eggs, 1 cup milk" -- and the
 * rows that were only marked opened are named separately, because nothing was
 * subtracted from those and claiming otherwise is the guess this feature
 * refuses to make.
 */
function CookedNote({ result, busy, onUndo }) {
  const used = (result.deductions || []).filter((d) => d.kind === 'consumed');
  const touched = (result.deductions || []).filter((d) => d.kind === 'used_some');

  return (
    <div className="alert info" role="status">
      <div className="row-between" style={{ gap: 10, alignItems: 'flex-start' }}>
        <div className="small grow" style={{ minWidth: 0 }}>
          {used.length === 0 && touched.length === 0
            ? 'Cooked. Nothing on the shelves matched, so nothing was deducted.'
            : (
              <>
                {used.length > 0 && (
                  <span>
                    <strong>Used: </strong>
                    {used.map((d) => `${formatQty(d.quantity, d.unit)} ${d.name}`).join(', ')}
                  </span>
                )}
                {touched.length > 0 && (
                  <span className="muted">
                    {used.length > 0 ? ' · ' : ''}
                    touched: {touched.map((d) => d.name).join(', ')}
                  </span>
                )}
              </>
            )}
        </div>
        <button type="button" className="small" disabled={busy} onClick={onUndo}>Undo</button>
      </div>
    </div>
  );
}

/**
 * Pointing one ingredient at the right master item. Only the link is editable
 * here: the quantity and the wording belong to the recipe text, which the edit
 * screen owns.
 */
function MatchIngredient({ recipeId, row, onClose, onSaved }) {
  const [item, setItem] = useState(
    row.master_item_id ? { id: row.master_item_id, name: row.item_name_matched } : null,
  );
  const { busy, error, run } = useAction();

  const save = (masterItemId) => run(async () => {
    await api.patch(`/recipes/${recipeId}/ingredients/${row.ingredient_id}`, {
      master_item_id: masterItemId,
    });
    onSaved();
  });

  // A pick saves straight away, an emptied picker does not. ItemPicker reports
  // both through onChange, and null there means "let me search again" -- which
  // is not the same intent as the unlink button below.
  const choose = (picked) => {
    setItem(picked);
    if (picked) save(picked.id);
  };

  return (
    <Modal title="Match this ingredient" onClose={onClose}>
      <div className="stack">
        <ErrorNote error={error} />

        <div className="card" style={{ background: 'var(--surface-2)' }}>
          <div className="tiny muted">The recipe says</div>
          <div>{row.raw_text || row.item_name}</div>
        </div>

        <label>
          <span className="label-text">Is this…</span>
          {/* No "create it" here: a match points at something that already
              exists, and stocking a new item is inventory's job, not a recipe's. */}
          <ItemPicker value={item} onChange={choose} allowCreate={false} autoFocus={!item} />
        </label>

        {busy && <div className="small muted"><span className="spinner" /> Saving</div>}

        <p className="tiny muted" style={{ margin: 0 }}>
          Confirming teaches the app this wording, so the next recipe that uses it matches on its own.
        </p>

        {/* Deliberately no "unlink" button. Analyzing a recipe re-links what
            it can, so a bare unlink undid itself on the very reload that
            confirmed it -- the honest correction is pointing at the RIGHT
            item, which is what the picker above is for. */}
      </div>
    </Modal>
  );
}

function AddToList({ recipeId, servings, onClose }) {
  const lists = useApi('/shopping-lists');
  const [listId, setListId] = useState('');
  const [includeUncertain, setIncludeUncertain] = useState(false);
  const [result, setResult] = useState(null);
  const { busy, error, run } = useAction();

  const active = (lists.data?.lists || []).filter((list) => list.status === 'active');
  const target = listId || active[0]?.id;

  const add = () => run(async () => {
    let id = target;
    if (!id) {
      const created = await api.post('/shopping-lists', {});
      id = created.id;
    }
    const response = await api.post(`/shopping-lists/${id}/from-recipe`, {
      recipe_id: recipeId,
      servings: servings || undefined,
      include_uncertain: includeUncertain,
    });
    setResult(response);
  });

  return (
    <Modal title="Add to shopping list" onClose={onClose}>
      <div className="stack">
        <ErrorNote error={error} />

        {result ? (
          <>
            <div className="alert info">
              Added {result.added}, merged {result.merged} into existing lines.
              {result.skipped_already_have > 0 && ` Skipped ${result.skipped_already_have} you already have.`}
            </div>
            {result.uncertain > 0 && !includeUncertain && (
              <div className="alert warn small">
                {result.uncertain} ingredient{result.uncertain === 1 ? '' : 's'} couldn&rsquo;t be checked against
                your pantry and {result.uncertain === 1 ? 'was' : 'were'} left off.
              </div>
            )}
            <Link className="button primary full" to="/shopping" onClick={onClose}>Open the list</Link>
          </>
        ) : (
          <>
            {active.length > 0 && (
              <label>
                <span className="label-text">List</span>
                <select value={target || ''} onChange={(event) => setListId(event.target.value)}>
                  {active.map((list) => (
                    <option key={list.id} value={list.id}>{list.name} ({list.remaining} left)</option>
                  ))}
                </select>
              </label>
            )}
            <label className="checkbox">
              <input
                type="checkbox"
                checked={includeUncertain}
                onChange={(event) => setIncludeUncertain(event.target.checked)}
              />
              <span>Also add things I can&rsquo;t verify</span>
            </label>
            <p className="tiny muted" style={{ margin: 0 }}>
              An ingredient counts as unverifiable when your stock is in units that can&rsquo;t be compared to
              the recipe&rsquo;s — 2 cans against 4 cups, for instance.
            </p>
            <button type="button" className="primary full" disabled={busy} onClick={add}>
              {busy ? <><span className="spinner" /> Adding</> : 'Add missing items'}
            </button>
          </>
        )}
      </div>
    </Modal>
  );
}
